'use client'
import React, { useState } from 'react'
import type { Media as MediaType } from '@/payload-types'
import { Mail, Phone, MessageCircle } from 'lucide-react'
import { Media } from '@/components/Media'

interface SimpleContactBlockType {
  headline?: string | null
  description?: string | null
  envelopeImage?: number | MediaType | null
  formFields?: {
    nameLabel?: string | null
    emailLabel?: string | null
    phoneLabel?: string | null
    serviceLabel?: string | null
    messageLabel?: string | null
    submitButtonText?: string | null
  } | null
  services?: Array<{ value: string; label: string }> | null
  contactOptions?: {
    sectionHeadline?: string | null
    sectionDescription?: string | null
    emailCard?: {
      title?: string | null
      description?: string | null
      email?: string | null
    } | null
    phoneCard?: {
      title?: string | null
      description?: string | null
      phone?: string | null
    } | null
    chatCard?: {
      title?: string | null
      description?: string | null
      buttonText?: string | null
    } | null
  } | null
  blockType: 'simpleContactBlock'
}

type Props = {
  className?: string
} & SimpleContactBlockType

export const SimpleContactBlockComponent: React.FC<Props> = ({
  headline,
  description,
  envelopeImage,
  formFields,
  services,
  contactOptions,
  className,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Handle form submission here
    console.log('Form submitted:', formData)
    
    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false)
      alert('Tack för ditt meddelande! Vi återkommer så snart som möjligt.')
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      })
    }, 1000)
  }


  return (
    <div className={className}>
      {/* Contact Hero Section */}
      <section className="relative bg-white py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text & Form */}
            <div className="max-w-xl">
              <h2 className="text-5xl font-bold text-[#211F54] mb-6">
                {headline}
              </h2>
              {description && (
                <p className="text-xl text-gray-600 mb-10">
                  {description}
                </p>
              )}
              
              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {formFields?.nameLabel || 'Namn'}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-[#0066CC]"
                      placeholder="Ditt namn"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {formFields?.emailLabel || 'E-post'}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-[#0066CC]"
                      placeholder="din@email.se"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      {formFields?.phoneLabel || 'Telefon'}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-[#0066CC]"
                      placeholder="070 123 45 67"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      {formFields?.serviceLabel || 'Tjänst'}
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-[#0066CC]"
                    >
                      <option value="">Välj tjänst</option>
                      {services?.map((service, index) => (
                        <option key={index} value={service.value || ''}>
                          {service.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {formFields?.messageLabel || 'Meddelande'}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0075FF] focus:border-[#0075FF] resize-none"
                    placeholder="Berätta mer om vad du behöver hjälp med..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto px-8 py-3 bg-[#0066CC] text-white rounded-full hover:bg-[#211F54] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Skickar...' : (formFields?.submitButtonText || 'Skicka meddelande')}
                </button>
              </form>
            </div>
            
            {/* Right Column - Envelope Image */}
            <div className="relative hidden lg:block">
              <div className="relative w-full h-[600px]">
                {envelopeImage && typeof envelopeImage !== 'string' ? (
                  <Media
                    resource={envelopeImage}
                    imgClassName="w-full h-full object-contain"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-gray-500">Ladda upp kuvertbild i Payload CMS</p>
                      <p className="text-sm text-gray-400 mt-2">Gå till admin-panelen för att lägga till bild</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Options Section */}
      {contactOptions && (
        <section className="py-20 bg-gray-50">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#211F54] mb-4">
                {contactOptions.sectionHeadline}
              </h2>
              {contactOptions.sectionDescription && (
                <p className="text-gray-600 max-w-3xl mx-auto">
                  {contactOptions.sectionDescription}
                </p>
              )}
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Email Card */}
              {contactOptions.emailCard && (
                <a
                  href={`mailto:${contactOptions.emailCard.email}`}
                  className="group block bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0066CC] rounded-2xl mb-6 group-hover:bg-[#211F54] transition-colors">
                    <Mail className="w-10 h-10 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#211F54] mb-3">
                    {contactOptions.emailCard.title}
                  </h3>
                  
                  {contactOptions.emailCard.description && (
                    <p className="text-gray-600 mb-4">
                      {contactOptions.emailCard.description}
                    </p>
                  )}
                  
                  <div className="font-medium text-[#0066CC] group-hover:underline">
                    {contactOptions.emailCard.email}
                  </div>
                </a>
              )}
              
              {/* Phone Card */}
              {contactOptions.phoneCard && (
                <a
                  href={`tel:${contactOptions.phoneCard.phone?.replace(/\s/g, '')}`}
                  className="group block bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0066CC] rounded-2xl mb-6 group-hover:bg-[#211F54] transition-colors">
                    <Phone className="w-10 h-10 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#211F54] mb-3">
                    {contactOptions.phoneCard.title}
                  </h3>
                  
                  {contactOptions.phoneCard.description && (
                    <p className="text-gray-600 mb-4">
                      {contactOptions.phoneCard.description}
                    </p>
                  )}
                  
                  <div className="font-medium text-[#0066CC] group-hover:underline">
                    {contactOptions.phoneCard.phone}
                  </div>
                </a>
              )}
              
              {/* Chat Card */}
              {contactOptions.chatCard && (
                <div className="group block bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0066CC] rounded-2xl mb-6 group-hover:bg-[#211F54] transition-colors">
                    <MessageCircle className="w-10 h-10 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#211F54] mb-3">
                    {contactOptions.chatCard.title}
                  </h3>
                  
                  {contactOptions.chatCard.description && (
                    <p className="text-gray-600 mb-4">
                      {contactOptions.chatCard.description}
                    </p>
                  )}
                  
                  <div className="font-medium text-[#0066CC] group-hover:underline">
                    {contactOptions.chatCard.buttonText}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      
    </div>
  )
}